import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Building2, Mail, Phone, Globe, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useCompanyStore } from '@/store/companyStore';
import { useContactStore } from '@/store/contactStore';
import { useActivityStore } from '@/store/activityStore';
import { ActivityTimeline } from '@/components/activities/ActivityTimeline';

export function CompanyDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { selectedCompany, fetchCompanyById } = useCompanyStore();
  const { fetchContactsByCompany } = useContactStore();
  const { fetchActivitiesByEntity } = useActivityStore();
  const [contacts, setContacts] = useState<any[]>([]);
  const [activities, setActivities] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      if (id) {
        setIsLoading(true);
        await fetchCompanyById(id);
        const contactList = await fetchContactsByCompany(id);
        setContacts(contactList);
        const acts = await fetchActivitiesByEntity('company', id);
        setActivities(acts);
        setIsLoading(false);
      }
    };
    loadData();
  }, [id, fetchCompanyById, fetchContactsByCompany, fetchActivitiesByEntity]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!selectedCompany) {
    return <div className="p-6">Company not found</div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/companies')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
            <Building2 className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">{selectedCompany.name}</h1>
            <p className="text-muted-foreground">{selectedCompany.industry || 'No industry'}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Tabs defaultValue="contacts">
            <TabsList>
              <TabsTrigger value="contacts">Contacts ({contacts.length})</TabsTrigger>
              <TabsTrigger value="activities">Activities</TabsTrigger>
            </TabsList>
            <TabsContent value="contacts" className="mt-4">
              {contacts.length === 0 ? (
                <p className="text-muted-foreground">No contacts added</p>
              ) : (
                <div className="space-y-2">
                  {contacts.map((contact) => (
                    <Card key={contact.id} className="cursor-pointer hover:bg-muted/50" onClick={() => navigate(`/contacts/${contact.id}`)}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">{contact.first_name} {contact.last_name}</p>
                            <p className="text-sm text-muted-foreground">{contact.job_title || 'No title'}</p>
                          </div>
                          <p className="text-sm">{contact.email}</p>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
            <TabsContent value="activities" className="mt-4">
              <ActivityTimeline activities={activities} />
            </TabsContent>
          </Tabs>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Company Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {selectedCompany.email && (
                <div className="flex items-center gap-3">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <a href={`mailto:${selectedCompany.email}`} className="text-primary hover:underline">
                    {selectedCompany.email}
                  </a>
                </div>
              )}
              {selectedCompany.phone && (
                <div className="flex items-center gap-3">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <span>{selectedCompany.phone}</span>
                </div>
              )}
              {selectedCompany.website && (
                <div className="flex items-center gap-3">
                  <Globe className="h-4 w-4 text-muted-foreground" />
                  <a href={selectedCompany.website} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                    {selectedCompany.website}
                  </a>
                </div>
              )}
              {selectedCompany.city && (
                <div className="flex items-center gap-3">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  <span>{selectedCompany.city}{selectedCompany.country ? `, ${selectedCompany.country}` : ''}</span>
                </div>
              )}
              <div className="flex items-center justify-between pt-4 border-t">
                <span className="text-muted-foreground">Size</span>
                <span>{selectedCompany.size || '-'}</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
